/**
 * 手游 Top Charts 筛选器 hover 文案配置。
 *
 * 文案定义见 _locales 下 infoDoc.ts 的 dbi_info_mobile_filter_*_hover 系列 key。
 * 通过 specialParams.filterTips 传入 CommonFilter，最终写入各筛选项的 hoverTip。
 * 仅用于 /topCharts/mobileGames/* 榜单页，勿在其他模块复用。
 */
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import {
  SENSOR_TOWER_TAG_KEYS,
  APPMAGIC_TAG_KEYS,
  RETENTION_D_RANGE_HOVER_MAP,
  RETENTION_D_RANGE_KEYS,
} from '@intelligence/components/Filter/FilterBase/_config';
import {
  buildFilterTips,
  mergeFilterTips,
  type TTopChartsFilterTipsFormatMessage,
} from '@intelligence/pages/TopCharts/_config/topChartsFilterTipsUtils';

/** 基础筛选项：filter key → locale id */
const BASE_LOCALE_KEY_MAP: Record<string, string> = {
  [filterKeyMap.MY_FILTER]: 'dbi_info_mobile_filter_my_filter_hover',
  [filterKeyMap.RANGE_TIME]: 'dbi_info_mobile_filter_range_time_hover',
  [filterKeyMap.REGION]: 'dbi_info_mobile_filter_region_hover',
  // 与 REGION 共用同一文案（组合国家选择器）
  [filterKeyMap.COMPOSED_COUNTRY_SELECT]: 'dbi_info_mobile_filter_region_hover',
  [filterKeyMap.PLATFORM]: 'dbi_info_mobile_filter_platform_hover',
  [filterKeyMap.GENRETOR]: 'dbi_info_mobile_filter_genre_hover',
  [filterKeyMap.ALL_Games]: 'dbi_info_mobile_filter_all_games_hover',
  [filterKeyMap.DATASOURCE]: 'dbi_info_mobile_filter_data_source_hover',
  // 与 DATASOURCE 共用同一文案（简化版数据源筛选）
  [filterKeyMap.DATASOURCESIM]: 'dbi_info_mobile_filter_data_source_hover',
  [filterKeyMap.ALL_COMPANYS]: 'dbi_info_mobile_filter_company_hover',
  [filterKeyMap.RELEASE_TIME]: 'dbi_info_mobile_filter_release_time_hover',
  // [filterKeyMap.REVENUE_SCOPE]: 'dbi_info_mobile_filter_revenue_scope_net_hover',
  [filterKeyMap.ACTIVE_USERS_RANGE]: 'dbi_info_mobile_filter_active_users_range_hover',
  [filterKeyMap.REVENUE_RANGE]: 'dbi_info_mobile_filter_revenue_range_hover',
  [filterKeyMap.ARPU_RANGE]: 'dbi_info_mobile_filter_arpu_range_hover',
  [filterKeyMap.DOWNLOADS_RANGE]: 'dbi_info_mobile_filter_downloads_range_hover',
  [filterKeyMap.RPD_RANGE]: 'dbi_info_mobile_filter_rpd_range_hover',
  [filterKeyMap.REVENUE_GROWTH]: 'dbi_info_mobile_filter_revenue_growth_hover',
  [filterKeyMap.GAME_ENGINE]: 'dbi_info_mobile_filter_game_engine_hover',
};

/** Sensor Tower 标签组：filter key → locale id */
const SENSOR_TOWER_LOCALE_KEY_MAP: Record<string, string> = {
  [filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE]:
    'dbi_info_mobile_filter_sensor_tower_art_style_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV]:
    'dbi_info_mobile_filter_sensor_tower_camera_pov_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]:
    'dbi_info_mobile_filter_sensor_tower_product_model_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_SETTING]:
    'dbi_info_mobile_filter_sensor_tower_setting_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_TAGS]:
    'dbi_info_mobile_filter_sensor_tower_tags_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_THEME]:
    'dbi_info_mobile_filter_sensor_tower_theme_hover',
  [filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION]:
    'dbi_info_mobile_filter_sensor_tower_monetization_hover',
};

/** App Magic 标签组：filter key → locale id */
const APPMAGIC_LOCALE_KEY_MAP: Record<string, string> = {
  [filterKeyMap.APPMAGIC_TAG_THEMES]: 'dbi_info_mobile_filter_appmagic_themes_hover',
  [filterKeyMap.APPMAGIC_TAG_FEATURES]: 'dbi_info_mobile_filter_appmagic_features_hover',
  [filterKeyMap.APPMAGIC_TAG_IP]: 'dbi_info_mobile_filter_appmagic_ip_hover',
  [filterKeyMap.APPMAGIC_TAG_SETTINGS]: 'dbi_info_mobile_filter_appmagic_settings_hover',
  [filterKeyMap.APPMAGIC_TAG_ART_STYLES]: 'dbi_info_mobile_filter_appmagic_art_styles_hover',
};

/**
 * 生成手游 Top Charts 各筛选项 hover 文案。
 * @returns filterKey → 已翻译文案，供 specialParams.filterTips 使用
 */
export const getMobileTopChartsFilterTips = (
  formatMessage: TTopChartsFilterTipsFormatMessage,
): Record<string, string> => {
  const tips = buildFilterTips(formatMessage, BASE_LOCALE_KEY_MAP);
  mergeFilterTips(tips, formatMessage, SENSOR_TOWER_LOCALE_KEY_MAP, SENSOR_TOWER_TAG_KEYS);
  mergeFilterTips(tips, formatMessage, APPMAGIC_LOCALE_KEY_MAP, APPMAGIC_TAG_KEYS);
  mergeFilterTips(tips, formatMessage, RETENTION_D_RANGE_HOVER_MAP, RETENTION_D_RANGE_KEYS);
  return tips;
};
